import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Search,
  Globe,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  RefreshCw,
  Zap,
  FileText,
  ExternalLink,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IndexingPage() {
  const { currentSite, sites } = useSite();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (currentSite?.id) {
      fetchHistory();
      fetchStatus();
    }
  }, [currentSite]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = currentSite?.id ? `?site_id=${currentSite.id}` : '';
      const response = await axios.get(`${API_URL}/api/indexing/history${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchHistory(), fetchStatus()]);
      toast.success('Actualizat');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchStatus = async () => {
    if (!currentSite?.id) return;
    
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/indexing/status/${currentSite.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const submitUrl = async () => {
    if (!url.trim()) {
      toast.error('Introdu un URL valid');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/indexing/submit-url`,
        { url: url.trim(), site_id: currentSite?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successCount = response.data.total_success || 0;
      toast.success(`URL trimis pentru indexare! ${successCount} servicii au acceptat.`);
      setUrl('');
      fetchHistory();
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimitere');
    } finally {
      setLoading(false);
    }
  };

  const bulkIndex = async () => {
    if (!currentSite?.id) {
      toast.error('Selectează un site');
      return;
    }

    setBulkLoading(true);
    toast.info('Trimit toate articolele pentru indexare...');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/indexing/bulk-submit`,
        { site_id: currentSite.id },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      
      toast.success(`${response.data.urls_submitted} articole trimise pentru indexare!`);
      fetchHistory();
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la indexare în masă');
    } finally {
      setBulkLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSuccess = (item) => {
    if (item.success === true) return true;
    if ((item.total_success ?? 0) > 0) return true;
    if (item.indexnow?.success?.length > 0) return true;
    if (item.services?.google_indexing_api?.success) return true;
    if (item.services?.indexnow?.results?.success?.length > 0) return true;
    if (item.services?.sitemap_ping?.success) return true;
    // bulk format: sitemap_ping.success e array direct (nu sub .services)
    if (item.sitemap_ping?.success?.length > 0) return true;
    // bulk google indexing API (din quota management)
    if ((item.google_indexing?.submitted ?? 0) > 0) return true;
    if ((item.google_indexing?.skipped_duplicate ?? 0) > 0) return true;
    return false;
  };

  const getServiceBadge = (item) => {
    if (!item) return null;
    const badges = [];

    // IndexNow — bulk salvează direct .success[], single URL salvează via .results.success[]
    const indexnowSuccess =
      Array.isArray(item.services?.indexnow?.results?.success) ? item.services.indexnow.results.success :
      Array.isArray(item.indexnow?.success) ? item.indexnow.success :
      [];
    indexnowSuccess.forEach(s => {
      badges.push(
        <Badge key={s} className="bg-green-500/20 text-green-500 border-green-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {s}
        </Badge>
      );
    });

    // Sitemap ping — .ping_results.success[] (single) sau .success[] (bulk)
    const pingSuccess =
      Array.isArray(item.services?.sitemap_ping?.ping_results?.success) ? item.services.sitemap_ping.ping_results.success :
      Array.isArray(item.sitemap_ping?.success) ? item.sitemap_ping.success :
      [];
    pingSuccess.forEach(s => {
      badges.push(
        <Badge key={`ping-${s}`} className="bg-blue-500/20 text-blue-500 border-blue-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {s} (ping)
        </Badge>
      );
    });

    // Google Indexing API — single URL (sub .services)
    if (item.services?.google_indexing_api?.success) {
      const skipped = item.services.google_indexing_api.skipped;
      badges.push(
        <Badge key="google-api" className="bg-green-500/20 text-green-500 border-green-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          {skipped ? 'Google API (deja indexat)' : 'Google API'}
        </Badge>
      );
    } else if (item.services?.google_indexing_api && !item.services.google_indexing_api.success) {
      const reason = item.services.google_indexing_api.reason;
      if (reason === 'quota_limit' || reason === 'bulk_quota_limit') {
        badges.push(
          <Badge key="google-quota" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Google (cotă epuizată)
          </Badge>
        );
      }
    }

    // Google Indexing API — bulk format (sub .google_indexing direct)
    if ((item.google_indexing?.submitted ?? 0) > 0) {
      badges.push(
        <Badge key="google-bulk" className="bg-green-500/20 text-green-500 border-green-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Google API ({item.google_indexing.submitted} URL-uri)
        </Badge>
      );
    }
    if ((item.google_indexing?.skipped_duplicate ?? 0) > 0) {
      badges.push(
        <Badge key="google-skip" className="bg-blue-500/20 text-blue-500 border-blue-500/30">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Google (deja indexate: {item.google_indexing.skipped_duplicate})
        </Badge>
      );
    }
    if (item.google_indexing?.quota_exhausted) {
      badges.push(
        <Badge key="google-exhausted" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Google (cotă epuizată)
        </Badge>
      );
    }

    return badges;
  };

  return (
    <div className="space-y-6" data-testid="indexing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold flex items-center gap-3">
          <Search className="w-8 h-8 text-primary" />
          Indexare Google & Bing
        </h1>
        <p className="text-muted-foreground mt-1">
          Trimite automat articolele pentru indexare rapidă
          {currentSite && <span className="text-primary ml-1">• {currentSite.site_name || currentSite.site_url}</span>}
        </p>
      </div>

      {/* Status Card */}
      {currentSite && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Status Indexare - {currentSite.site_name || currentSite.site_url}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : status ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-4">
                <div className="p-4 bg-secondary/50 rounded-lg text-center">
                  <div className="text-2xl font-bold">{status.total_articles}</div>
                  <span className="text-sm text-muted-foreground">Articole Publicate</span>
                </div>
                <div className="p-4 bg-secondary/50 rounded-lg text-center">
                  <div className="text-2xl font-bold">{status.indexing_requests}</div>
                  <span className="text-sm text-muted-foreground">Cereri Indexare</span>
                </div>
                <div className="p-4 bg-secondary/50 rounded-lg text-center">
                  <div className="text-sm font-medium">Ultima Indexare</div>
                  <span className="text-sm text-muted-foreground">
                    {status.last_indexed ? formatDate(status.last_indexed) : 'Niciodată'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Selectează un site pentru a vedea statusul
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit URL */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Trimite URL pentru Indexare
          </CardTitle>
          <CardDescription>
            Trimite un URL specific sau toate articolele site-ului
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="https://site-ul-tau.com/articol-nou"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="bg-secondary border-border"
                data-testid="indexing-url-input"
              />
            </div>
            <Button
              onClick={submitUrl}
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
              data-testid="submit-url-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Trimit...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Trimite URL
                </>
              )}
            </Button>
          </div>
          
          {currentSite && (
            <div className="pt-4 border-t border-border">
              <Button
                onClick={bulkIndex}
                disabled={bulkLoading}
                variant="outline"
                className="w-full border-green-500/50 text-green-500 hover:bg-green-500/10"
                data-testid="bulk-index-btn"
              >
                {bulkLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Indexez toate articolele...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Indexează Toate Articolele din {currentSite.site_name || 'Site'}
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Trimite toate articolele publicate către Google, Bing și Yandex pentru indexare rapidă
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-blue-500 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-500 mb-2">Cum funcționează indexarea automată?</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• <strong>IndexNow</strong> - Protocol instant pentru Bing, Yandex (rezultate în minute)</li>
                <li>• <strong>Sitemap Ping</strong> - Notifică Google și Bing despre actualizări</li>
                <li>• <strong>Auto-indexare</strong> - Fiecare articol nou e trimis automat la publicare</li>
                <li>• Indexarea nu garantează poziția în rezultate, doar că Google știe de pagină</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Istoric Indexare
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Se actualizează...' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nicio cerere de indexare încă</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-secondary/50 rounded-lg"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {isSuccess(item) ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="font-medium truncate max-w-[300px]">
                        {item.type === 'bulk'
                          ? (item.google_indexing?.submitted ?? 0) === 0
                            ? `Verificare zilnică: ${item.google_indexing?.skipped_duplicate ?? item.urls_submitted} articole, toate indexate`
                            : `Indexare în masă (${item.urls_submitted} URL-uri)`
                          : (item.article_title || item.url)
                        }
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {getServiceBadge(item)}
                    {item.total_success != null && (
                      <Badge variant="outline">
                        {item.total_success} servicii reușite
                      </Badge>
                    )}
                  </div>
                  
                  {item.url && (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Deschide URL
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
